import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiLock, FiAlertCircle, FiCreditCard, FiChevronRight } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/currency";
import { validateCart } from "../utils/cartValidation";
import { createOrder, generateAdminEmailPayload, buildMailtoLink } from "../utils/orderUtils";
import { saveToStorage } from "../utils/localStorage";
import InputField, { SelectField } from "../components/ui/InputField";
import Button from "../components/ui/Button";

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

const COUNTRIES = ["United States", "Canada", "United Kingdom", "Australia", "Germany", "France", "Japan", "Other"];

const initialForm = {
  // Contact
  firstName: "", lastName: "", email: "", phone: "",
  // Shipping
  shippingAddress: "", shippingCity: "", shippingState: "", shippingZip: "", shippingCountry: "United States",
  // Billing
  billingSameAsShipping: true,
  billingAddress: "", billingCity: "", billingState: "", billingZip: "", billingCountry: "United States",
  // Payment
  cardholderName: "", cardNumber: "", cardExpiry: "", cardCvv: "",
};

const formatCardNumber = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
};

const required = (v) => (!v?.trim() ? "This field is required" : null);
const validEmail = (v) => (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "Enter a valid email" : null);
const validCard = (v) => (v.replace(/\s/g, "").length < 16 ? "Enter a valid 16-digit card number" : null);
const validExpiry = (v) => (!/^\d{2}\/\d{2}$/.test(v) ? "Use MM/YY format" : null);
const validCvv = (v) => (v.length < 3 ? "CVV must be 3-4 digits" : null);
const validZip = (v) => (!/^\d{5}(-\d{4})?$/.test(v) ? "Enter a valid ZIP code" : null);

const CheckoutPage = () => {
  const { items, subtotal, savings, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=contact, 2=shipping, 3=payment

  const validation = validateCart(subtotal);
  const shippingCost = subtotal >= 99 ? 0 : 20;
  const total = subtotal + shippingCost;

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  };

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      errs.firstName = required(form.firstName);
      errs.lastName = required(form.lastName);
      errs.email = required(form.email) || validEmail(form.email);
    }
    if (s === 2) {
      errs.shippingAddress = required(form.shippingAddress);
      errs.shippingCity = required(form.shippingCity);
      errs.shippingState = required(form.shippingState);
      errs.shippingZip = required(form.shippingZip) || validZip(form.shippingZip);
      errs.shippingCountry = required(form.shippingCountry);
      if (!form.billingSameAsShipping) {
        errs.billingAddress = required(form.billingAddress);
        errs.billingCity = required(form.billingCity);
        errs.billingState = required(form.billingState);
        errs.billingZip = required(form.billingZip) || validZip(form.billingZip);
      }
    }
    if (s === 3) {
      errs.cardholderName = required(form.cardholderName);
      errs.cardNumber = required(form.cardNumber) || validCard(form.cardNumber);
      errs.cardExpiry = required(form.cardExpiry) || validExpiry(form.cardExpiry);
      errs.cardCvv = required(form.cardCvv) || validCvv(form.cardCvv);
    }
    const hasErrors = Object.values(errs).some(Boolean);
    setErrors(errs);
    return !hasErrors;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((s) => s + 1);
  };

  const handlePlaceOrder = async () => {
    if (!validateStep(3)) return;
    if (!validation.valid) return;

    setLoading(true);
    // Simulate async order processing
    await new Promise((r) => setTimeout(r, 1200));

    const order = createOrder({ cartItems: items, subtotal, formData: form });
    const emailPayload = generateAdminEmailPayload(order);
    const mailtoLink = buildMailtoLink(emailPayload);

    // Save to localStorage
    saveToStorage("ep_last_order", order);
    saveToStorage("ep_admin_email_payload", emailPayload);
    saveToStorage("ep_admin_mailto", mailtoLink);

    clearCart();
    setLoading(false);
    navigate("/order-success");
  };

  if (items.length === 0 && !loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-5xl">🛒</p>
        <h2 className="text-2xl font-bold">Your cart is empty</h2>
        <Link to="/shop" className="btn-primary">Shop Now</Link>
      </div>
    );
  }

  const steps = ["Contact", "Shipping", "Payment"];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2 text-brand-600 mb-2">
          <FiLock size={16} />
          <span className="text-sm font-semibold">Secure Checkout</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-0 mb-10 max-w-sm mx-auto">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step > i + 1 ? "bg-emerald-500 text-white" :
                  step === i + 1 ? "bg-brand-600 text-white" :
                  "bg-gray-200 text-gray-500"
                }`}
              >
                {step > i + 1 ? "✓" : i + 1}
              </div>
              <span className={`text-xs font-medium ${step === i + 1 ? "text-brand-600" : "text-gray-400"}`}>{s}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-2 mb-4 transition-all ${step > i + 1 ? "bg-emerald-500" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Cart validation */}
      {!validation.valid && (
        <div className="max-w-2xl mx-auto mb-6 flex items-start gap-2.5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <FiAlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Cart validation failed</p>
            <p>{validation.message}</p>
            <Link to="/cart" className="underline mt-1 inline-block">Edit your cart</Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="card p-6 md:p-8">
            {/* Step 1: Contact */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="First Name" required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} error={errors.firstName} placeholder="John" />
                    <InputField label="Last Name" required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} error={errors.lastName} placeholder="Doe" />
                  </div>
                  <InputField label="Email Address" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} error={errors.email} placeholder="john@example.com" />
                  <InputField label="Phone Number" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} error={errors.phone} placeholder="+1 (555) 000-0000" />
                </div>
                <div className="mt-8 flex justify-end">
                  <Button onClick={nextStep}>
                    Continue to Shipping <FiChevronRight size={16} />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Shipping + Billing */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className="text-xl font-bold text-gray-900 mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <InputField label="Street Address" required value={form.shippingAddress} onChange={(e) => set("shippingAddress", e.target.value)} error={errors.shippingAddress} placeholder="123 Main Street, Apt 4B" />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="City" required value={form.shippingCity} onChange={(e) => set("shippingCity", e.target.value)} error={errors.shippingCity} placeholder="New York" />
                    <SelectField label="State" required value={form.shippingState} onChange={(e) => set("shippingState", e.target.value)} error={errors.shippingState}>
                      <option value="">Select state</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </SelectField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="ZIP Code" required value={form.shippingZip} onChange={(e) => set("shippingZip", e.target.value)} error={errors.shippingZip} placeholder="10001" />
                    <SelectField label="Country" required value={form.shippingCountry} onChange={(e) => set("shippingCountry", e.target.value)} error={errors.shippingCountry}>
                      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    </SelectField>
                  </div>
                </div>

                {/* Billing */}
                <div className="mt-8 mb-4">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.billingSameAsShipping}
                      onChange={(e) => set("billingSameAsShipping", e.target.checked)}
                      className="w-4 h-4 accent-brand-600 cursor-pointer"
                    />
                    <span className="font-medium text-gray-700">Billing address same as shipping</span>
                  </label>
                </div>

                {!form.billingSameAsShipping && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 mt-4">
                    <h3 className="font-bold text-gray-900">Billing Address</h3>
                    <InputField label="Street Address" required value={form.billingAddress} onChange={(e) => set("billingAddress", e.target.value)} error={errors.billingAddress} placeholder="123 Main Street" />
                    <div className="grid grid-cols-2 gap-4">
                      <InputField label="City" required value={form.billingCity} onChange={(e) => set("billingCity", e.target.value)} error={errors.billingCity} placeholder="New York" />
                      <SelectField label="State" required value={form.billingState} onChange={(e) => set("billingState", e.target.value)} error={errors.billingState}>
                        <option value="">Select state</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </SelectField>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField label="ZIP Code" required value={form.billingZip} onChange={(e) => set("billingZip", e.target.value)} error={errors.billingZip} placeholder="10001" />
                      <SelectField label="Country" required value={form.billingCountry} onChange={(e) => set("billingCountry", e.target.value)}>
                        {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                      </SelectField>
                    </div>
                  </motion.div>
                )}

                <div className="mt-8 flex justify-between">
                  <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
                  <Button onClick={nextStep}>Continue to Payment <FiChevronRight size={16} /></Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <div className="flex items-center gap-2 mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Payment</h2>
                  <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-600 font-medium px-2 py-0.5 rounded-full">
                    <FiLock size={11} /> SSL Secured
                  </span>
                </div>

                {/* Card preview */}
                <div className="bg-gradient-to-br from-brand-700 to-brand-500 rounded-2xl p-6 mb-6 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <FiCreditCard size={28} className="mb-4 opacity-80" />
                  <p className="font-mono text-lg tracking-widest mb-4">
                    {form.cardNumber || "**** **** **** ****"}
                  </p>
                  <div className="flex justify-between text-sm">
                    <div>
                      <p className="text-white/60 text-xs">Cardholder</p>
                      <p className="font-semibold">{form.cardholderName || "YOUR NAME"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 text-xs">Expires</p>
                      <p className="font-semibold">{form.cardExpiry || "MM/YY"}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <InputField
                    label="Cardholder Name"
                    required
                    value={form.cardholderName}
                    onChange={(e) => set("cardholderName", e.target.value.toUpperCase())}
                    error={errors.cardholderName}
                    placeholder="JOHN DOE"
                  />
                  <InputField
                    label="Card Number"
                    required
                    value={form.cardNumber}
                    onChange={(e) => set("cardNumber", formatCardNumber(e.target.value))}
                    error={errors.cardNumber}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Expiry Date"
                      required
                      value={form.cardExpiry}
                      onChange={(e) => set("cardExpiry", formatExpiry(e.target.value))}
                      error={errors.cardExpiry}
                      placeholder="MM/YY"
                      maxLength={5}
                    />
                    <InputField
                      label="CVV"
                      required
                      type="password"
                      value={form.cardCvv}
                      onChange={(e) => set("cardCvv", e.target.value.replace(/\D/g, "").slice(0, 4))}
                      error={errors.cardCvv}
                      placeholder="•••"
                      maxLength={4}
                    />
                  </div>
                </div>

                {/* <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700">
                  <p className="font-semibold mb-1">🔒 Demo Mode</p>
                  <p>This is a frontend simulation. No real payment is processed. Enter any 16-digit card number to proceed.</p>
                </div> */}

                <div className="mt-8 flex justify-between">
                  <Button variant="ghost" onClick={() => setStep(2)}>← Back</Button>
                  <Button onClick={handlePlaceOrder} loading={loading} disabled={!validation.valid}>
                    {loading ? "Placing Order…" : `Place Order · ${formatCurrency(subtotal)}`}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Order summary */}
        <div className="lg:col-span-1">
          <div className="card p-5 sticky top-24">
            <h2 className="font-bold text-base text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-3 mb-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 items-center">
                  <div className="relative">
                    <img src={item.image} alt={item.title} className="w-14 h-14 object-cover rounded-xl bg-gray-100" />
                    <span className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {item.quantity}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 line-clamp-1">{item.title}</p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
              {savings > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Savings</span>
                  <span>−{formatCurrency(savings)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                {shippingCost === 0
                  ? <span className="text-emerald-600 font-medium">FREE</span>
                  : <span>{formatCurrency(shippingCost)}</span>
                }
              </div>
              <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-100">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-400 text-center space-y-1">
              <p>🔒 SSL encrypted checkout</p>
              <p>🔒 128-bit SSL encryption</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
